import { useEffect, useMemo, useState } from "react";
import {
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
} from "@mui/material";
import CircleIcon from "@mui/icons-material/Circle";
import CloseIcon from "@mui/icons-material/Close";
import UndoIcon from "@mui/icons-material/Undo";
import { useTranslation } from "react-i18next";
import GenericButton from "@/components/forms/inputs/button/genericButton";
import styles from "@/components/appointmentRequest/appointmentRequestSubjectsModal.module.css";
import { SubjectSearchSelect } from "@/components/subjectSearch/subjectSearchSelect";
import { useSubjectSearch } from "@/helper/customHooks/useSubjectSearch";
import ExportMenu, {
  ExportMenuItem,
} from "@/components/appointmentRequest/exportMenu";
import { useModalDismiss } from "@/helper/customHooks/useModalDismiss";

export type SubjectModalType = "invited" | "excluded";

interface SubjectInfo {
  id: string | number;
  name: string;
  appointment_created?: boolean;
  subsequently_excluded?: boolean;
  subsequently_removed?: boolean;
}

interface AppointmentRequestSubjectsModalProps {
  open: boolean;
  subjectModalType: SubjectModalType;
  invitedSubjects: SubjectInfo[];
  numberOfAllInvitedSubjects: number;
  excludedSubjects: SubjectInfo[];
  numberOfAllExcludedSubjects: number;
  selectedExcludedSubjectIds: string[];
  excludedSubjectIdsBeingRemoved: string[];
  isBatchRemovingExcludedSubjects: boolean;
  onToggleExcludedSubjectSelection: (subjectId: string) => void;
  onRemoveExcludedSubject: (subjectId: string) => void;
  onRemoveSelectedExcludedSubjects: (subjectIds: string[]) => void;
  onClearExcludedSelection: () => void;
  onClose: () => void;
  appointmentRequestId?: string;
  handleError: (error: any) => void;
  handleNotification: (
    variant: "success" | "error" | "warning" | "info",
    message: string,
    success: boolean
  ) => void;
}

export default function AppointmentRequestSubjectsModal(
  props: AppointmentRequestSubjectsModalProps
) {
  const { t } = useTranslation();
  const MAX_VISIBLE_SUBJECTS = 50;
  const [searchInput, setSearchInput] = useState<string>("");
  const [matchedSearchIds, setMatchedSearchIds] = useState<Set<string> | null>(
    null
  );

  const isExcludedMode = props.subjectModalType === "excluded";
  const hasExcludedSubjects = props.excludedSubjects.length > 0;

  const { fetchSubjects } = useSubjectSearch({
    subdomain: "admin",
    allowSubjectSearchForNonAdmin: false,
  });

  const searchableAllowedIds = useMemo(() => {
    const source =
      props.subjectModalType === "excluded"
        ? props.excludedSubjects
        : props.invitedSubjects;
    return new Set(source.map((s) => String(s.id)));
  }, [props.subjectModalType, props.excludedSubjects, props.invitedSubjects]);

  const filteredInvitedSubjects = useMemo(() => {
    if (!matchedSearchIds) return props.invitedSubjects;
    return props.invitedSubjects.filter((s) =>
      matchedSearchIds.has(String(s.id))
    );
  }, [matchedSearchIds, props.invitedSubjects]);

  const filteredExcludedSubjects = useMemo(() => {
    if (!matchedSearchIds) return props.excludedSubjects;
    return props.excludedSubjects.filter((s) =>
      matchedSearchIds.has(String(s.id))
    );
  }, [matchedSearchIds, props.excludedSubjects]);

  useEffect(() => {
    if (!props.open) {
      setSearchInput("");
      setMatchedSearchIds(null);
    }
  }, [props.open]);

  useEffect(() => {
    if (!props.open) return;

    const query = searchInput.trim();
    if (query.length < 3) {
      setMatchedSearchIds(null);
      return;
    }

    let isActive = true;
    const timeoutId = setTimeout(() => {
      fetchSubjects(query)
        .then((options) => {
          if (!isActive) return;
          const allowedIds = options
            .map((opt: any) => String(opt.value))
            .filter((id: string) => searchableAllowedIds.has(id));
          setMatchedSearchIds(new Set(allowedIds));
        })
        .catch(() => {
          if (!isActive) return;
          setMatchedSearchIds(new Set());
        });
    }, 500);

    return () => {
      isActive = false;
      clearTimeout(timeoutId);
    };
  }, [fetchSubjects, props.open, searchInput, searchableAllowedIds]);

  const shouldShowSearch =
    props.subjectModalType === "invited"
      ? props.numberOfAllInvitedSubjects > 0
      : props.numberOfAllExcludedSubjects > 0;

  const searchRow = shouldShowSearch ? (
    <ListItem disablePadding className={styles.searchRow}>
      <SubjectSearchSelect
        value={null}
        onChange={() => undefined}
        loadOptions={() => Promise.resolve([])}
        inputValue={searchInput}
        onInputChange={(value: string) => setSearchInput(value)}
        showOptionsMenu={false}
        placeholder={t("search_placeholder")}
        isClearable={true}
      />
    </ListItem>
  ) : null;

  const exportMenuItems: ExportMenuItem[] = [
    {
      label: "export_as",
      format: "csv",
      lang: "de",
    },
    { isSeparator: true },
    {
      label: "export_as",
      format: "xlsx",
      lang: "de",
      icon: "chart",
    },
  ];

  const cancelClose = useModalDismiss(props.open, props.onClose);

  return (
    <Dialog
      open={props.open}
      scroll="paper"
      component={"div"}
      maxWidth={false}
      {...cancelClose}
      PaperProps={{
        sx: {
          width: "50vw",
          maxWidth: "50vw",
        },
      }}
    >
      <DialogTitle className={styles.dialogTitle}>
        <div className={styles.headerContainer}>
          {props.subjectModalType === "invited" ? (
            <>
              {/* spacer */}
              <div />
              <span>{t("invited_users")}</span>
              <div className={styles.actionWrapper}>
                <ExportMenu
                  entity="appointmentRequest"
                  exportParameter="export_subject_info_set"
                  handleError={props.handleError}
                  handleNotification={props.handleNotification}
                  menuItems={exportMenuItems}
                  entityId={props.appointmentRequestId}
                />
              </div>
            </>
          ) : (
            <span className={styles.titleTextFull}>{t("excluded_users")}</span>
          )}
        </div>
      </DialogTitle>
      <DialogContent dividers>
        <DialogContentText
          component={"div"}
          className={styles.dialogContentText}
        >
          {props.subjectModalType === "invited" &&
            props.numberOfAllInvitedSubjects > 0 && (
              <ListItemText
                primary={t(
                  props.numberOfAllInvitedSubjects === 1
                    ? "appointmentrequest_all_invited_persons_phrase"
                    : "appointmentrequest_all_invited_persons_phrase_multi",
                  { number: props.numberOfAllInvitedSubjects }
                )}
                primaryTypographyProps={{
                  className: styles.primaryTypography,
                }}
              />
            )}
          {props.subjectModalType === "excluded" &&
            props.numberOfAllExcludedSubjects > 0 && (
              <ListItemText
                primary={t(
                  props.numberOfAllExcludedSubjects === 1
                    ? "appointmentrequest_all_excluded_persons_phrase"
                    : "appointmentrequest_all_excluded_persons_phrase_multi",
                  { number: props.numberOfAllExcludedSubjects }
                )}
                primaryTypographyProps={{ className: styles.primaryTypography }}
              />
            )}
          {(props.subjectModalType === "invited"
            ? props.numberOfAllInvitedSubjects > 0
            : props.numberOfAllExcludedSubjects > 0) && searchRow}
          {isExcludedMode && !hasExcludedSubjects ? (
            <List>
              <ListItemText
                primary={t("appointmentrequest_no_excluded_subjects")}
                primaryTypographyProps={{
                  className: styles.primaryTypographyNoExcludedSubjects,
                }}
              />
            </List>
          ) : (
            <div className={styles.listScrollArea}>
              <List>
                {props.subjectModalType === "invited" &&
                  filteredInvitedSubjects
                    .slice(0, MAX_VISIBLE_SUBJECTS)
                    .map((subject) => {
                      const statusKey = subject.subsequently_excluded
                        ? "appointmentrequest_subject_withdrew_suffix"
                        : subject.subsequently_removed
                        ? "appointmentrequest_subject_removed_suffix"
                        : null;
                      return (
                        <ListItem key={subject.id} disablePadding>
                          <ListItemIcon>
                            {subject.appointment_created ? (
                              <CircleIcon color="success" fontSize="small" />
                            ) : subject.subsequently_excluded ||
                              subject.subsequently_removed ? (
                              <CloseIcon color="error" fontSize="small" />
                            ) : (
                              <CircleIcon color="error" fontSize="small" />
                            )}
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <>
                                {subject.name}
                                {statusKey && (
                                  <>
                                    {" "}
                                    <em>{t(statusKey)}</em>
                                  </>
                                )}
                              </>
                            }
                          />
                        </ListItem>
                      );
                    })}

                {props.subjectModalType === "invited" &&
                  filteredInvitedSubjects.length > MAX_VISIBLE_SUBJECTS && (
                    <ListItemText
                      primary={t(
                        filteredInvitedSubjects.length -
                          MAX_VISIBLE_SUBJECTS ===
                          1
                          ? "sppointmentrequest_all_invited_persons_additional_invited_one"
                          : "sppointmentrequest_all_invited_persons_additional_invited_other",
                        {
                          number:
                            filteredInvitedSubjects.length -
                            MAX_VISIBLE_SUBJECTS,
                        }
                      )}
                      primaryTypographyProps={{
                        className: styles.primaryTypography,
                      }}
                    />
                  )}

                {props.subjectModalType === "excluded" &&
                  filteredExcludedSubjects
                    .slice(0, MAX_VISIBLE_SUBJECTS)
                    .map((subject) => {
                      const subjectId = String(subject.id);
                      const isRemoved =
                        props.excludedSubjectIdsBeingRemoved.includes(
                          subjectId
                        );
                      return (
                        <ListItem key={subject.id} disablePadding>
                          <div className={styles.subjectListWrapper}>
                            <Checkbox
                              checked={props.selectedExcludedSubjectIds.includes(
                                subjectId
                              )}
                              disabled={isRemoved}
                              onChange={() =>
                                props.onToggleExcludedSubjectSelection(
                                  subjectId
                                )
                              }
                            />
                            <Tooltip title={t("remove")}>
                              <span>
                                <GenericButton
                                  variant="icon"
                                  color="iconBase"
                                  disabled={isRemoved}
                                  onClick={() =>
                                    props.onRemoveExcludedSubject(subjectId)
                                  }
                                >
                                  <UndoIcon />
                                </GenericButton>
                              </span>
                            </Tooltip>
                            <ListItemText
                              primary={subject.name}
                              sx={{
                                margin: 0,
                                minWidth: 0,
                                flex: 1,
                                "& .MuiTypography-root": {
                                  whiteSpace: "normal",
                                  wordBreak: "break-word",
                                },
                              }}
                            />
                          </div>
                        </ListItem>
                      );
                    })}

                {props.subjectModalType === "excluded" &&
                  filteredExcludedSubjects.length > MAX_VISIBLE_SUBJECTS && (
                    <ListItemText
                      primary={t(
                        filteredExcludedSubjects.length -
                          MAX_VISIBLE_SUBJECTS ===
                          1
                          ? "appointmentrequest_all_excluded_persons_additional_excluded_one"
                          : "appointmentrequest_all_excluded_persons_additional_excluded_other",
                        {
                          number:
                            filteredExcludedSubjects.length -
                            MAX_VISIBLE_SUBJECTS,
                        }
                      )}
                      primaryTypographyProps={{
                        className: styles.primaryTypography,
                      }}
                    />
                  )}
              </List>
            </div>
          )}
        </DialogContentText>
      </DialogContent>
      {isExcludedMode && hasExcludedSubjects ? (
        <DialogActions className={styles.dialogActionsMulti}>
          <GenericButton
            variant="outlined"
            disabled={props.isBatchRemovingExcludedSubjects}
            onClick={() => {
              props.onClearExcludedSelection();
              props.onClose();
            }}
          >
            {t("cancel")}
          </GenericButton>
          <GenericButton
            variant="contained"
            disabled={
              props.isBatchRemovingExcludedSubjects ||
              props.selectedExcludedSubjectIds.length === 0
            }
            onClick={() =>
              props.onRemoveSelectedExcludedSubjects(
                props.selectedExcludedSubjectIds
              )
            }
          >
            {t("remove")}
          </GenericButton>
        </DialogActions>
      ) : (
        <DialogActions className={styles.dialogActionsSingle}>
          <GenericButton variant="contained" onClick={props.onClose}>
            {t("ok_capital")}
          </GenericButton>
        </DialogActions>
      )}
    </Dialog>
  );
}
